import React from "react";
import { Breadcrumbs, Link as MuiLink, Typography } from "@mui/material";
import { Link, useLocation } from "react-router-dom";

/**
 * Extender este mapa para cualquier nueva sección del admin.
 */
const SEGMENT_NAMES: Record<string, string> = {
  admin: "Dashboard",
  articulos: "Artículos",
  categorias: "Categorías",
  new: "Nuevo Artículo",
  edit: "Editar Artículo",
  mensajes: "Mensajes",
  usuarios: "Usuarios"
};

/**
 * Detecta si un segmento es un ID dinámico (número o UUID, por ejemplo en el editar articulo).
 * Estos segmentos se omiten del breadcrumb para mayor claridad.
 */
const isDynamicId = (segment: string): boolean =>
  /^\d+$/.test(segment) ||
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment);

interface Crumb {
  name: string;
  to: string;
  isLast: boolean;
}


export default function BreadcrumbsNav() {
  const location = useLocation();

  const rawSegments = location.pathname.split("/").filter(Boolean);

  // Filtra IDs dinámicos y construye los crumbs con rutas acumulativas
  const crumbs: Crumb[] = [];
  let cumulativePath = "";

  rawSegments.forEach((segment) => {
    // Omitir IDs dinámicos: no aportan contexto al usuario
    if (isDynamicId(segment)) return;

    cumulativePath += `/${segment}`;
    const name = SEGMENT_NAMES[segment] ?? segment.charAt(0).toUpperCase() + segment.slice(1);

    crumbs.push({ name, to: cumulativePath, isLast: false });
  });

  // El último crumb es el actual (no clickeable)
  if (crumbs.length > 0) {
    crumbs[crumbs.length - 1] = { ...crumbs[crumbs.length - 1], isLast: true };
  }

  // No renderizar si hay un solo segmento (solo "Admin") o ninguno
  if (crumbs.length <= 1) return null;

  return (
    <Breadcrumbs
      aria-label="breadcrumb"
      sx={{
        mb: 3,
        "& .MuiBreadcrumbs-separator": { color: "text.disabled" },
      }}
    >
      {crumbs.map((crumb, idx) =>
        crumb.isLast ? (
          <Typography
            key={idx}
            variant="body2"
            color="text.primary"
            sx={{ fontWeight: 600 }}
          >
            {crumb.name}
          </Typography>
        ) : (
          <MuiLink
            key={idx}
            component={Link}
            to={crumb.to}
            underline="hover"
            color="text.secondary"
            variant="body2"
            sx={{
              "&:hover": { color: "primary.main" },
              transition: "color 0.2s",
            }}
          >
            {crumb.name}
          </MuiLink>
        )
      )}
    </Breadcrumbs>
  );
}
